import { ModuleMultiSelect } from '../module-multiselect';
import { useEffect, useMemo, useRef, useState } from 'react';
import { MultiSelect } from 'primereact/multiselect';
import { Dropdown } from 'primereact/dropdown';
import { useManagedModuleEditor } from '../../providers/managed-module-management-provider';
import { Panel } from 'primereact/panel';
import { DebouncedInput } from '../debounced-input';
import { EstimatedDurationField } from '../estimated-duration-field';
import '../../assets/ContentEditor.css';

export function GeneralPropsPanel() {
    const { managedModule, allModules, updateModuleField, updateModuleThemes, updateModulePrerequisites,
        allModulesLoading, validationErrors, themeOptions, validationTarget } = useManagedModuleEditor();
    const [collapsed, setCollapsed] = useState((managedModule?.fragments ?? []).length > 0);
    const rootRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (validationTarget?.issue.scope === 'module') setCollapsed(false);
    }, [validationTarget]);
    useEffect(() => {
        const issue = validationTarget?.issue;
        if (collapsed || allModulesLoading || issue?.scope !== 'module' || issue.field === 'fragments') return;
        const field = issue.field;
        const ids = { name: 'name', description: 'description', instructions: 'instructions',
            estimated_minutes: 'estimated-minutes', prerequisites: 'prerequisites', finalQuizFragmentKey: 'final-quiz-fragment' };
        const frame = requestAnimationFrame(() => {
            const input = rootRef.current?.querySelector<HTMLElement>(`#${ids[field]}`);
            input?.scrollIntoView?.({ block: 'nearest' });
            input?.focus();
        });
        return () => cancelAnimationFrame(frame);
    }, [validationTarget, collapsed, allModulesLoading]);

    const prerequisiteModules = useMemo(() => {
        return allModules
            .filter(c => c.module_key !== managedModule.module_key)
            .map(c => ({
                label: c.name,
                value: c.module_key,
                managed: c.is_managed
            }));
    }, [managedModule, allModules]);

    if (allModulesLoading) {
        return <div style={{ padding: 16 }}>Loading...</div>;
    }

    return (<>
        <div ref={rootRef}>
        <Panel className="managed-general-properties" header='General properties (hit plus/minus to toggle) ==>' toggleable
            collapsed={collapsed} onToggle={event => setCollapsed(event.value)}>
        <div className="p-fluid">
            {/* Row 1: Name and Theme */}
            <div className="grid">
                <div className="col-12 md:col-6 field">
                    <label htmlFor="name">Name *</label>
                    <DebouncedInput
                        isTextArea={false}
                        id="name"
                        initialValue={managedModule.name ?? ''}
                        maxLength={250}
                        required
                        onSync={(val: string) => updateModuleField("name", val)}
                        className={validationErrors.name ? "p-invalid" : ""}
                        syncStrategy="blur"
                    />
                    {validationErrors.name && <small className="p-error">{validationErrors.name}</small>}
                </div>
                <div className="col-12 md:col-6 field">
                    <label htmlFor="theme">Themes</label>
                    <MultiSelect
                        value={managedModule.theme ?? []}
                        options={themeOptions}
                        optionLabel="label"
                        dataKey="value" 
                        useOptionAsValue={true}
                        onChange={(e) => {
                            updateModuleThemes(e.value);
                        }}
                        placeholder="Select available themes"
                        className="w-full"
                        display="chip"
                    />
                </div>
            </div>

            {/* Row 2: Description and Instructions */}
            <EstimatedDurationField compact value={managedModule.estimated_minutes}
                onChange={value => updateModuleField('estimated_minutes', value)} error={validationErrors.estimated_minutes} />
            <div className="grid">
                <div className="col-12 md:col-6 field">
                    <label htmlFor="description">Description *</label>
                    <small className="block text-color-secondary">Markdown supported: **bold**, *italic*, lists and links.</small>
                    <DebouncedInput
                        isTextArea={true}
                        id="description"
                        initialValue={managedModule.description ?? ''}
                        rows={2}
                        maxLength={4094}
                        onSync={(val: string) => updateModuleField("description", val)}
                        className={validationErrors.description ? "p-invalid" : ""}
                        syncStrategy="blur"
                    />
                </div>
                <div className="col-12 md:col-6 field">
                    <label htmlFor="instructions">Instructions *</label>
                    <small className="block text-color-secondary">Markdown supported. Existing HTML instructions are preserved.</small>
                    <DebouncedInput
                        isTextArea={true}
                        id="instructions"
                        initialValue={managedModule.instructions ?? ''}
                        rows={2}
                        maxLength={4094}
                        onSync={(val: string) => updateModuleField("instructions", val)}
                        className={validationErrors.instructions ? "p-invalid" : ""}
                        syncStrategy="blur"
                    />
                </div>
            </div>

            {/* Row 3: Pre-requisites and final quiz */}
            <div className="grid">
            <div className="col-12 md:col-6 field">
                <label htmlFor="prerequisites">Pre-requisite modules</label>
                <ModuleMultiSelect
                    inputId="prerequisites"
                    value={managedModule.prerequisites?.map(p => p.module_key)}
                    options={prerequisiteModules}
                    onChange={updateModulePrerequisites}
                    placeholder="Select prerequisite modules"
                    className="w-full"
                    display="chip"
                />
            </div>
            <div className="col-12 md:col-6 field">
                <label htmlFor="final-quiz-fragment">Final quiz fragment</label>
                <Dropdown inputId="final-quiz-fragment" aria-label="Final quiz fragment"
                    optionLabel="label" optionValue="value"
                    value={managedModule.finalQuizFragmentKey ?? null}
                    options={[
                        { label: 'None', value: null },
                        ...[...(managedModule.fragments ?? [])].sort((a, b) => a.order - b.order)
                            .map(fragment => ({ label: fragment.title || `Fragment ${fragment.order + 1}`, value: fragment.key }))
                    ]}
                    onChange={event => updateModuleField('finalQuizFragmentKey', event.value ?? null)}
                    placeholder="None" showClear className={validationErrors.finalQuizFragmentKey ? 'w-full p-invalid' : 'w-full'} />
                {validationErrors.finalQuizFragmentKey && <small className="p-error">{validationErrors.finalQuizFragmentKey}</small>}
            </div>
            </div>
        </div>
    </Panel>
    </div>
    </>);
}
